import { Component, OnInit, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { BasisTheoryService } from '../../../services/basistheory';
import { PaymentProcessService } from '../../../services/payment-process.service';
import { LayoutUtility } from '../../../../../shared/utils';

@Component({
  selector: 'kt-bridgepay-bank-account-form',
  templateUrl: './bank-account-form.component.html',
  styleUrls: ['./bank-account-form.component.scss'],
})
export class BankAccountFormComponent implements OnInit, OnDestroy {
  @Output() formSubmit = new EventEmitter<{
    type: string;
    token: any;
    savePaymentMethod: boolean;
    zip: string;
    brand: string;
    cardLast4: string;
  }>();

  @Input() tenantId: string;
  @Input() showSubmitButton = true;
  @Input() showSavePaymentMethodOption = false;

  accountHolderNameEmpty = false;
  bankNameEmpty = false;
  routingNumberEmpty = false;
  accountNumberEmpty = false;

  private btInstance?: any;
  private accountNameElement?: any;
  private accountNumberElement?: any;
  private routingNumberElement?: any;
  private bankNameElement?: any;
  private zipCodeElement?: any;

  isFormValid = false;
  isSubmitting = false;
  validationMessage: string | null = null;
  savePaymentMethod = false;

  private readyStates: { [key: string]: boolean } = {};

  constructor(
    private layoutUtility: LayoutUtility,
    private basisTheoryService: BasisTheoryService,
    private processPaymentService: PaymentProcessService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.processPaymentService.triggerGenerateToken$.subscribe(() => {
      this.onSubmit();
    });

    try {
      this.btInstance = await this.basisTheoryService.initialize('');

      this.accountNameElement = this.createElement('text', 'accountNameElement', {
        autoComplete: 'name',
      });

      this.accountNumberElement = this.createElement('text', 'accountNumberElement', {
        inputMode: 'numeric',
      });

      this.routingNumberElement = this.createElement('text', 'routingNumberElement', {
        inputMode: 'numeric',
        mask: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/],
      });

      this.bankNameElement = this.createElement('text', 'bankNameElement', {});

      this.zipCodeElement = this.createElement('text', 'zipCodeElement', {
        inputMode: 'numeric',
      });

      await Promise.all([
        this.accountNameElement.mount('#accountNameElement'),
        this.accountNumberElement.mount('#accountNumberElement'),
        this.routingNumberElement.mount('#routingNumberElement'),
        this.bankNameElement.mount('#bankNameElement'),
        this.zipCodeElement.mount('#zipCodeElement'),
      ]);

      // Add validation checks
      const validateForm = () => {
        this.isFormValid = [
          this.accountNameElement,
          this.accountNumberElement,
          this.routingNumberElement,
          this.bankNameElement,
          this.zipCodeElement,
        ].every((element) => element?.metadata?.valid);
      };

      this.setupAutoSubmitListener(this.accountNameElement, validateForm);
      this.setupAutoSubmitListener(this.accountNumberElement, validateForm);
      this.setupAutoSubmitListener(this.routingNumberElement, validateForm);
      this.setupAutoSubmitListener(this.bankNameElement, validateForm);
      this.setupAutoSubmitListener(this.zipCodeElement, validateForm);

      this.setupReadyListener(this.accountNameElement, 'accountName');
      this.setupReadyListener(this.accountNumberElement, 'accountNumber');
      this.setupReadyListener(this.routingNumberElement, 'routingNumber');
      this.setupReadyListener(this.bankNameElement, 'bankName');
      this.setupReadyListener(this.zipCodeElement, 'zipCode');
    } catch (error) {
      this.handleError(error);
    }
  }

  private createElement(type: string, targetId: string, config: any): any {
    return this.btInstance?.createElement(type, {
      ...config,
      targetId,
      style: this.basisTheoryService.COMMON_STYLE,
    });
  }

  async onSubmit(): Promise<void> {
    if (this.checkFormEmpty()) return;
    // if (!this.isFormValid) {
    //   this.validationMessage = 'Please complete all required fields';
    //   return;
    // }

    this.isSubmitting = true;
    this.validationMessage = null;

    await this.generateToken();

    this.isSubmitting = false;
  }

  private handleError(error: any): void {
    this.validationMessage = error.message || 'Failed to process bank account';
  }

  private async generateToken(): Promise<void> {
    try {
      const tenantContainerPath = `/tenant-${this.tenantId.toLowerCase()}/bank/`; // Normalize to lowercase as per Basis Theory docs
      const token = await this.btInstance?.tokens.create({
        type: 'bank',
        data: {
          account_name: this.accountNameElement,
          account_number: this.accountNumberElement,
          routing_number: this.routingNumberElement,
          bank_name: this.bankNameElement,
          zip: this.zipCodeElement,
        },
        fingerprintExpression: '{{ data.account_number }}|{{ data.routing_number }}',
        deduplicateToken: true,
        mask: {
          account_number: '{{ data.account_number | reveal_last: 4 }}',
          routing_number: '{{ data.routing_number | reveal_last: 4 }}',
        },
        containers: [tenantContainerPath],
      });

      this.formSubmit.emit({
        token,
        type: 'ach',
        savePaymentMethod: this.savePaymentMethod,
        zip: this.zipCodeElement.value,
        brand: 'ach',
        cardLast4: this.accountNumberElement?._cardMetadata?.cardLast4,
      });
    } catch (error) {
      this.handleError(error);
      if (error)
        this.layoutUtility.showActionNotification('ERROR', 'Failed to process bank account');
    }
  }

  ngOnDestroy(): void {
    [
      this.accountNameElement,
      this.accountNumberElement,
      this.routingNumberElement,
      this.bankNameElement,
      this.zipCodeElement,
    ].forEach((element) => element?.unmount());
  }

  private setupAutoSubmitListener(element: any, customCallback?: (event: any) => void): void {
    element.on('change', (event: any) => {
      if (customCallback) customCallback(event);
      this.resetForm();
    });
  }

  private setupReadyListener(
    element: any,
    key: string,
    customCallback?: (event: any) => void,
  ): void {
    element.on('ready', (event: any) => {
      // Mark the element as ready
      this.readyStates[key] = true;
      if (customCallback) {
        customCallback(event);
      }
    });
  }

  resetForm() {
    this.accountHolderNameEmpty = false;
    this.bankNameEmpty = false;
    this.routingNumberEmpty = false;
    this.accountNumberEmpty = false;
  }
  checkFormEmpty(): boolean {
    this.accountHolderNameEmpty = this.accountNameElement?.metadata.empty;
    this.bankNameEmpty = this.bankNameElement?.metadata.empty;
    this.routingNumberEmpty = this.routingNumberElement?.metadata.empty;
    this.accountNumberEmpty = this.accountNumberElement?.metadata.empty;
    return (
      this.accountHolderNameEmpty &&
      this.bankNameEmpty &&
      this.routingNumberEmpty &&
      this.accountNumberEmpty
    );
  }
}
